import { useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
}

export function Input({ label, error, helpText, id, className = "", type, ...props }: InputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id ?? label?.toLowerCase().replace(/\s/g, "-");
  const isPassword = type === "password";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  const inputClassName = `w-full rounded-lg border px-3 py-2 text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8] focus:ring-offset-0 ${
    error ? "border-red-500" : "border-gray-300 focus:border-[#1A3AB8]"
  } ${isPassword ? "pr-10" : ""} ${className}`;

  const inputElement = (
    <input
      id={inputId}
      type={inputType}
      className={inputClassName}
      aria-invalid={!!error}
      aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
      {...props}
    />
  );

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-[#2C2C2C]">
          {label}
        </label>
      )}
      {isPassword ? (
        <div className="relative">
          {inputElement}
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      ) : (
        inputElement
      )}
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
      {helpText && !error && (
        <p id={`${inputId}-help`} className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}
    </div>
  );
}
